import {
  Badge,
  Box,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Button,
  Container,
  Heading,
  HStack,
  Icon,
  SimpleGrid,
  Text,
  VStack,
  useColorModeValue,
} from "@chakra-ui/react";
import {
  FiArrowDown,
  FiArrowLeft,
  FiChevronLeft,
  FiHome,
} from "react-icons/fi";
import { Link as RouterLink, useNavigate, useParams } from "react-router-dom";
import { PageHero } from "../components/common/PageHero";
import { SectionWrapper } from "../components/common/SectionWrapper";
import { Footer } from "../components/landing/Footer";
import { Navbar } from "../components/landing/Navbar";
import { getServiceById } from "../data/services";

export const ServiceDetailPage = () => {
  const { serviceId } = useParams<{ serviceId: string }>();
  const navigate = useNavigate();
  const service = getServiceById(serviceId);

  const cardBg = useColorModeValue("white", "dark.50");
  const textColor = useColorModeValue("gray.800", "white");
  const subtitleColor = useColorModeValue("gray.600", "gray.400");
  const borderColor = useColorModeValue("gray.100", "gray.700");
  const accentBg = useColorModeValue(
    "radial-gradient(circle at 85% 45%, rgba(249,168,166,0.45), transparent 50%), radial-gradient(circle at 15% 85%, rgba(249,168,166,0.45), transparent 50%)",
    "radial-gradient(circle at 85% 15%, rgba(173,11,6,0.22), transparent 50%), radial-gradient(circle at 15% 85%, rgba(173,11,6,0.22), transparent 50%)",
  );
  const iconBg = useColorModeValue("white", "icon.bg");
  const iconColor = useColorModeValue("brand.500", "icon.svg");

  if (!service) {
    return (
      <Box dir="rtl" overflowX="hidden">
        <Navbar />
        <Box py={40}>
          <Container maxW="container.md">
            <VStack spacing={5} textAlign="center">
              <Heading color={textColor} fontSize="2xl">
                خدمت مورد نظر یافت نشد
              </Heading>
              <Text color={subtitleColor}>
                آدرس واردشده معتبر نیست یا هنوز برای این خدمت صفحه‌ای ثبت نشده
                است.
              </Text>
              <Button colorScheme="brand" onClick={() => navigate("/services")}>
                بازگشت به خدمات
              </Button>
            </VStack>
          </Container>
        </Box>
        <Footer />
      </Box>
    );
  }

  const scrollToAreas = () => {
    const el = document.getElementById("service-areas");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <Box dir="rtl" overflowX="hidden">
      <Navbar />
      <PageHero
        title={service.heroTitle}
        titleHighlight="تأمین سرمایه امید"
        subtitle={service.heroSubtitle}
        primaryAction={{
          label: "حوزه‌های خدمت",
          icon: FiArrowDown,
          onClick: scrollToAreas,
        }}
        secondaryAction={{
          label: service.ctaLabel,
          onClick: () => {
            if (service.ctaHref && service.ctaIsExternal) {
              window.open(service.ctaHref, "_blank", "noopener,noreferrer");
              return;
            }

            navigate(service.ctaHref || "/contact");
          },
        }}
      />

      <SectionWrapper py={{ base: 10, md: 14 }}>
        <Container maxW="container.xl">
          <VStack spacing={8} align="stretch">
            <Breadcrumb
              separator={
                <Icon
                  verticalAlign="middle"
                  as={FiChevronLeft as any}
                  color={subtitleColor}
                />
              }
              fontSize="sm"
              color={subtitleColor}
            >
              <BreadcrumbItem>
                <BreadcrumbLink as={RouterLink} to="/">
                  <HStack spacing={1}>
                    <Icon
                      verticalAlign="middle"
                      as={FiHome as any}
                      boxSize={3.5}
                    />
                    <Text>خانه</Text>
                  </HStack>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbItem>
                <BreadcrumbLink as={RouterLink} to="/services">
                  خدمات
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbItem isCurrentPage>
                <Text color={textColor}>{service.shortTitle}</Text>
              </BreadcrumbItem>
            </Breadcrumb>

            <SimpleGrid columns={{ base: 1, lg: 3 }} spacing={6}>
              <Box
                gridColumn={{ base: "auto", lg: "span 2" }}
                bg={cardBg}
                borderRadius="2xl"
                border="1px solid"
                borderColor={borderColor}
                p={{ base: 6, md: 8 }}
              >
                <VStack align="start" spacing={4}>
                  <Badge colorScheme={service.accentColor} px={3} py={1}>
                    {service.imageLabel}
                  </Badge>
                  <Heading
                    fontSize={{ base: "xl", md: "2xl" }}
                    color={textColor}
                  >
                    معرفی خدمت
                  </Heading>
                  <Text color={subtitleColor} lineHeight="2">
                    {service.intro}
                  </Text>
                </VStack>
              </Box>

              <Box
                bg={accentBg}
                borderRadius="2xl"
                p={{ base: 6, md: 8 }}
                border="1px solid"
                borderColor={borderColor}
              >
                <VStack align="start" spacing={4}>
                  <Box
                    w="48px"
                    h="48px"
                    borderRadius="xl"
                    bg={iconBg}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Icon
                      verticalAlign="middle"
                      as={service.icon as any}
                      boxSize={6}
                      color={iconColor}
                    />
                  </Box>
                  <Heading fontSize="lg" color={textColor}>
                    چرا این خدمت مهم است؟
                  </Heading>
                  <Text color={subtitleColor} lineHeight="2">
                    {service.description}
                  </Text>
                </VStack>
              </Box>
            </SimpleGrid>
          </VStack>
        </Container>
      </SectionWrapper>

      <SectionWrapper id="service-areas" alt>
        <Container maxW="container.xl">
          <VStack spacing={4} textAlign="center" mb={10}>
            <Heading as="h2" fontSize={{ base: "xl", md: "2xl", lg: "3xl" }}>
              حوزه‌های خدمت
            </Heading>
            <Text color={subtitleColor} maxW="720px">
              این صفحه بر اساس شرح خدمات و ساختار محتوایی تعریف‌شده برای{" "}
              {service.title} تنظیم شده است.
            </Text>
          </VStack>

          <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing={5}>
            {service.serviceAreas.map((area) => (
              <Box
                key={area}
                bg={cardBg}
                borderRadius="2xl"
                p={6}
                border="1px solid"
                borderColor={borderColor}
              >
                <HStack align="start" spacing={3}>
                  <Box
                    mt={1}
                    w={2.5}
                    h={2.5}
                    borderRadius="full"
                    bg="brand.500"
                    flexShrink={0}
                  />
                  <Text color={textColor} fontWeight="medium" lineHeight="1.9">
                    {area}
                  </Text>
                </HStack>
              </Box>
            ))}
          </SimpleGrid>
        </Container>
      </SectionWrapper>

      <SectionWrapper>
        <Container maxW="container.xl">
          <VStack spacing={4} textAlign="center" mb={10}>
            <Heading as="h2" fontSize={{ base: "xl", md: "2xl", lg: "3xl" }}>
              جزئیات و محورهای اجرایی
            </Heading>
            <Text color={subtitleColor} maxW="720px">
              هر خدمت بر مبنای مجموعه‌ای از زیرخدمت‌ها، فرایندهای اجرایی و
              خروجی‌های عملی تعریف شده است.
            </Text>
          </VStack>

          <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
            {service.sections.map((section) => (
              <Box
                key={section.title}
                bg={cardBg}
                borderRadius="2xl"
                p={{ base: 6, md: 7 }}
                border="1px solid"
                borderColor={borderColor}
                h="100%"
              >
                <VStack align="start" spacing={4}>
                  <Heading as="h3" fontSize="lg" color={textColor}>
                    {section.title}
                  </Heading>
                  {section.description && (
                    <Text color={subtitleColor} lineHeight="2">
                      {section.description}
                    </Text>
                  )}
                  {section.bullets && (
                    <VStack align="start" spacing={3} w="100%">
                      {section.bullets.map((bullet) => (
                        <HStack key={bullet} align="start" spacing={3}>
                          <Box
                            mt={1}
                            w={2}
                            h={2}
                            borderRadius="full"
                            bg="brand.500"
                            flexShrink={0}
                          />
                          <Text color={textColor} lineHeight="1.9">
                            {bullet}
                          </Text>
                        </HStack>
                      ))}
                    </VStack>
                  )}
                </VStack>
              </Box>
            ))}
          </SimpleGrid>
        </Container>
      </SectionWrapper>

      {service.showcase && service.showcase.length > 0 && (
        <SectionWrapper alt>
          <Container maxW="container.xl">
            <VStack spacing={4} textAlign="center" mb={10}>
              <Heading as="h2" fontSize={{ base: "xl", md: "2xl", lg: "3xl" }}>
                {service.showcaseTitle ?? "سوابق و نمونه‌ها"}
              </Heading>
              <Text color={subtitleColor} maxW="720px">
                بسته به ماهیت خدمت، این بخش می‌تواند شامل سوابق اجرایی، نمونه
                پروژه‌ها یا نمونه کاربردهای متداول باشد.
              </Text>
            </VStack>

            <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing={6}>
              {service.showcase.map((group) => (
                <Box
                  key={group.title}
                  bg={cardBg}
                  borderRadius="2xl"
                  p={{ base: 6, md: 7 }}
                  border="1px solid"
                  borderColor={borderColor}
                  h="100%"
                >
                  <VStack align="start" spacing={4}>
                    <Heading as="h3" fontSize="md" color={textColor}>
                      {group.title}
                    </Heading>
                    <VStack align="start" spacing={3}>
                      {group.items.map((item) => (
                        <HStack key={item} align="start" spacing={3}>
                          <Box
                            mt={1}
                            w={2}
                            h={2}
                            borderRadius="full"
                            bg="brand.500"
                            flexShrink={0}
                          />
                          <Text color={subtitleColor} lineHeight="1.9">
                            {item}
                          </Text>
                        </HStack>
                      ))}
                    </VStack>
                  </VStack>
                </Box>
              ))}
            </SimpleGrid>

            {service.note && (
              <Box
                mt={8}
                bg={cardBg}
                borderRadius="2xl"
                p={5}
                border="1px dashed"
                borderColor="brand.300"
              >
                <Text color={subtitleColor} lineHeight="1.9">
                  {service.note}
                </Text>
              </Box>
            )}
          </Container>
        </SectionWrapper>
      )}

      <SectionWrapper>
        <Container maxW="container.lg">
          <Box
            bgGradient="linear(135deg, brand.500, brand.700)"
            borderRadius="3xl"
            p={{ base: 8, md: 12 }}
            color="white"
            textAlign="center"
          >
            <VStack spacing={4}>
              <Heading as="h2" fontSize={{ base: "xl", md: "2xl" }}>
                {service.ctaTitle}
              </Heading>
              <Text color="whiteAlpha.900" lineHeight="2" maxW="720px">
                {service.ctaDescription}
              </Text>
              <HStack spacing={4} pt={2} flexWrap="wrap" justify="center">
                <Button
                  bg="white"
                  color="brand.600"
                  rightIcon={
                    <Icon verticalAlign="middle" as={FiArrowLeft as any} />
                  }
                  _hover={{ bg: "whiteAlpha.900" }}
                  onClick={() => {
                    if (service.ctaHref && service.ctaIsExternal) {
                      window.open(service.ctaHref, "_blank", "noopener,noreferrer");
                      return;
                    }

                    navigate(service.ctaHref || "/contact");
                  }}
                >
                  {service.ctaLabel}
                </Button>
                <Button
                  variant="outline"
                  color="white"
                  borderColor="whiteAlpha.600"
                  onClick={() => navigate("/services")}
                >
                  سایر خدمات
                </Button>
              </HStack>
            </VStack>
          </Box>
        </Container>
      </SectionWrapper>

      <Footer />
    </Box>
  );
};
